/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import 'babel-polyfill';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import { createHashHistory } from 'history';
import 'sanitize.css/sanitize.css';
import 'react-id-swiper/src/styles/css/swiper.css';
import queryString from 'query-string';

import '@ingka/svg-icon/dist/style.css';
import '@ingka/forms/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/focus/dist/style.css';

// Import root app
import Application from 'Application';

// Import Language Provider
import LanguageProvider from 'domain/LanguageProvider';

import Header from 'domain/Analytics/tealium/Header';
import Data from 'domain/Analytics/tealium/Data';
import GlobalStyle from './global-styles';

import configureStore from './configureStore';

// Import CSS reset and Global Styles
import './global-styles';
import '@ingka/focus/dist/style.css';

import version from './version.json';
const hash = window.location.hash;

const matchUrl = hash.match(/(\/([A-Za-z]*)\/([A-Za-z]*))([^?]*)/);
const [, basename, country, language, pathname] = matchUrl || ['/'];

const initialState = {};
const history = createHashHistory({ basename });
const store = configureStore(initialState, history);
const isRTL = language === 'he' || language === 'ar';

if (isRTL) {
  console.log('Identified RTL language:', language, ', setting app direction to RTL.');
  document.body.setAttribute('dir', 'rtl')
}

const MOUNT_NODE = document.getElementById('app');

const cwcalcReachedFromIpexGallery = (() => {
  const { ipexGallery } = queryString.parse(hash.substring(hash.indexOf('?') + 1));
  return ipexGallery && ipexGallery.toLowerCase() === 'true';
})();

const cwcalcReachedFromUpptacka = (() => {
  const { origin } = queryString.parse(hash.substring(hash.indexOf('?') + 1));
  return origin && origin.toLowerCase() === 'upptacka';
})();

const cwcalcReachedFromKiosk = (() => {
  const { origin } = queryString.parse(hash.substring(hash.indexOf('?') + 1));
  return origin && origin.toLowerCase() === 'kiosk';
})();


export const ipexGalleryEnabled = cwcalcReachedFromIpexGallery ? true : false;
export const upptackaEnabled = cwcalcReachedFromUpptacka ? true : false;
export const kioskEnabled = cwcalcReachedFromKiosk ? true : false;

const render = () => {
  ReactDOM.render(
    <div>
      <GlobalStyle />
      <Header>
        <Provider store={store}>
          <LanguageProvider country={country} language={language}>
            <Data>
              <ConnectedRouter history={history}>
                <Application />
              </ConnectedRouter>
              </Data>
          </LanguageProvider>
        </Provider>
      </Header>
    </div>,
    MOUNT_NODE
  );
};

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  (new Promise((resolve) => {
    resolve(import('intl'));
  }))
    .then(() => Promise.all([
      import('intl/locale-data/jsonp/en.js'),
    ]))
    .then(() => render())
    .catch((err) => {
      throw err;
    });
} else {
  render();
}
